<div class="flex flex-col gap-6" data-testid="enrichment-package-settings">
  @if (summary(); as pkg) {
  <section
    class="enr-bg-surface flex flex-col gap-5 rounded-lg p-6 shadow-sm"
    aria-labelledby="enrichment-package-facts-title"
    data-testid="enrichment-package-facts"
  >
    <div class="flex items-start gap-4">
      <span
        class="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#8bc129]/15 text-[#4a6b14]"
      >
        <mat-icon aria-hidden="true">inventory_2</mat-icon>
      </span>
      <div class="flex min-w-0 flex-1 flex-col">
        <h3
          id="enrichment-package-facts-title"
          class="enr-text-surface m-0 text-xs font-semibold uppercase tracking-wide"
        >
          About this package
        </h3>
        <p
          class="m-0 mt-1 min-w-0 text-xl font-semibold md:truncate"
          [title]="pkg.name"
          data-testid="enrichment-package-facts-name"
        >
          {{ pkg.name }}
        </p>
        <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          @if (pkg.status; as status) {
          <span
            class="inline-flex items-center gap-2 text-sm leading-5"
            [enrTooltip]="statusHint[status.tone]"
            enrTooltipPosition="bottom"
            data-testid="enrichment-package-facts-status"
          >
            @if (status.tone === "sellable") {
            <svg
              class="size-3.5 shrink-0"
              [class]="toneClass[status.tone].text"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              data-testid="enrichment-package-facts-sellable-icon"
            >
              <circle cx="8" cy="8" r="7" fill="currentColor" />
              <path
                d="M5 8.2l2 2 4-4.4"
                stroke="#fff"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            } @else {
            <span
              class="inline-flex size-2 shrink-0 rounded-full"
              [class]="toneClass[status.tone].dot"
              aria-hidden="true"
            ></span>
            }
            <span
              class="whitespace-nowrap font-semibold"
              [class]="toneClass[status.tone].text"
              >{{ status.label }}</span
            >
            @if (status.tone === "sellable") {
            <span
              class="whitespace-nowrap font-medium"
              [class]="toneClass[status.tone].text"
              >· sellable</span
            >
            }
          </span>
          } @if (pkg.version) {
          <enr-badge
            variant="soft"
            size="small"
            color="neutral"
            data-testid="enrichment-package-facts-version"
            >Catalog v{{ pkg.version }}</enr-badge
          >
          }
        </div>
      </div>
    </div>

    <dl class="m-0 grid gap-3 sm:grid-cols-3">
      <div
        class="flex flex-col gap-1.5 rounded-lg border border-white bg-white/60 px-4 py-3"
      >
        <dt
          class="enr-text-surface flex items-center gap-1.5 text-xs uppercase tracking-wide"
        >
          <mat-icon class="size-4! text-base! leading-none!" aria-hidden="true"
            >event</mat-icon
          >Valid
        </dt>
        <dd class="m-0" data-testid="enrichment-package-facts-validity">
          @if (pkg.validity; as validity) {
          <span
            class="inline-flex flex-wrap items-center gap-2"
            [enrTooltip]="validity.fullLabel"
            enrTooltipPosition="bottom"
          >
            <span class="text-base font-semibold">{{ validity.label }}</span>
            @if (validity.stateLabel) {
            <enr-badge
              variant="soft"
              size="small"
              [color]="validityBadgeColor[validity.state] ?? 'neutral'"
              >{{ validity.stateLabel }}</enr-badge
            >
            }
          </span>
          } @else {
          <span class="text-base font-semibold">—</span>
          }
        </dd>
      </div>
      <div
        class="flex flex-col gap-1.5 rounded-lg border border-white bg-white/60 px-4 py-3"
      >
        <dt
          class="enr-text-surface flex items-center gap-1.5 text-xs uppercase tracking-wide"
        >
          <mat-icon class="size-4! text-base! leading-none!" aria-hidden="true"
            >storefront</mat-icon
          >Sales channels
        </dt>
        <dd
          class="m-0 flex flex-wrap gap-1.5"
          data-testid="enrichment-package-facts-channels"
        >
          @for (channel of pkg.channels; track channel) {
          <enr-badge variant="soft" size="small" color="neutral"
            >{{ channel }}</enr-badge
          >
          } @empty {
          <span class="text-base font-semibold">—</span>
          }
        </dd>
      </div>
      <div
        class="flex flex-col gap-1.5 rounded-lg border border-white bg-white/60 px-4 py-3"
      >
        <dt
          class="enr-text-surface flex items-center gap-1.5 text-xs uppercase tracking-wide"
        >
          <mat-icon class="size-4! text-base! leading-none!" aria-hidden="true"
            >sell</mat-icon
          >Prices
        </dt>
        <dd
          class="m-0 text-base font-semibold"
          data-testid="enrichment-package-facts-prices"
        >
          {{ pricesLabel() }}
        </dd>
      </div>
    </dl>

    <dl
      class="m-0 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-gray-100 pt-5 md:grid-cols-4"
    >
      @for (fact of pkg.facts; track fact.label) {
      <div
        class="flex flex-col gap-1"
        [attr.data-readiness-target]="fact.readinessTarget ?? null"
      >
        <dt class="enr-text-surface text-xs uppercase tracking-wide">
          {{ fact.label }}
        </dt>
        <dd
          class="m-0 text-sm font-semibold"
          [class]="fact.value === '—' ? 'text-gray-400' : 'text-gray-900'"
        >
          {{ fact.value }}
        </dd>
      </div>
      }
    </dl>
  </section>
  } @if (priceCount() > 1) {
  <div
    class="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    role="note"
    data-testid="enrichment-package-settings-banner"
  >
    <span aria-hidden="true">⚠</span>
    <p class="m-0">
      <strong>Shared by all {{ priceCount() }} prices in this package.</strong>
      Changes here apply to every price@if (liveCount() > 0) {, including the {{
      liveCount() }} that {{ liveCount() === 1 ? "is" : "are" }} live}. Prices
      that override a value keep their own.
    </p>
  </div>
  }

  <app-enrichment-term />
  <app-enrichment-tab-product-attributes />
</div>
